import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { Upload, Trash2, Copy, Video } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface VideoFile {
  name: string;
  url: string;
  size: number;
  created_at: string;
}

export default function VideoManagement() {
  const { user } = useAuth();
  const [videos, setVideos] = useState<VideoFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchVideos();
    }
  }, [user]);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase.storage.from('videos').list();
      
      if (error) throw error;

      const videoFiles = data.map(file => ({
        name: file.name,
        url: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/videos/${file.name}`,
        size: file.metadata?.size || 0,
        created_at: file.created_at
      }));

      setVideos(videoFiles);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast({
        title: "Invalid file type",
        description: "Please upload a video file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (500MB limit)
    if (file.size > 524288000) {
      toast({
        title: "File too large",
        description: "Maximum file size is 500MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;

      // Upload in chunks to show progress for large files
      const chunkSize = 1024 * 1024 * 5; // 5MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const percent = ((i + 1) / totalChunks) * 100;
        setUploadProgress(percent);
      }

      const { error: uploadError } = await supabase.storage
        .from('videos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      toast({
        title: "Success",
        description: "Video uploaded successfully",
      });

      await fetchVideos();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm(`Are you sure you want to delete ${fileName}?`)) return;

    try {
      const { error } = await supabase.storage.from('videos').remove([fileName]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Video deleted successfully",
      });

      await fetchVideos();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Copied",
      description: "Video URL copied to clipboard",
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-6 w-6" />
            Video Management
          </CardTitle>
          <CardDescription>
            Upload and manage videos for your website. Maximum file size: 500MB
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <input
                type="file"
                id="video-upload"
                accept="video/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="hidden"
              />
              <label
                htmlFor="video-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  MP4, WebM, MOV up to 500MB
                </p>
              </label>

              {uploading && (
                <div className="mt-4 space-y-2">
                  <Progress value={uploadProgress} />
                  <p className="text-sm text-muted-foreground">
                    Uploading... {Math.round(uploadProgress)}%
                  </p>
                </div>
              )}
            </div>

            {/* Videos List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Uploaded Videos</h3>
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : videos.length === 0 ? (
                <p className="text-muted-foreground">No videos uploaded yet</p>
              ) : (
                <div className="grid gap-4">
                  {videos.map((video) => (
                    <Card key={video.name}>
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                          <div className="w-full md:w-48 aspect-video bg-muted rounded-lg overflow-hidden">
                            <video
                              src={video.url}
                              className="w-full h-full object-cover"
                              controls
                            />
                          </div>
                          <div className="flex-1 space-y-2">
                            <h4 className="font-medium">{video.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {formatFileSize(video.size)} • Uploaded{' '}
                              {new Date(video.created_at).toLocaleDateString()}
                            </p>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyUrl(video.url)}
                              >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy URL
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(video.name)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
